import { useEffect, useState, useCallback } from "react";
import {
  SideNavigation,
  SideNavigationProps,
  SpaceBetween,
  Tiles,
  TilesProps,
} from "@cloudscape-design/components-themed";
import { useNavigate } from "react-router-dom";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import {
  useGetAuthQuery,
  useIssueOnetimePasswordMutation,
} from "../../features/api";
import {
  OnetimePasswordResponse,
  OnetimePasswordRequest,
} from "../../features/api-interfaces";
import { SSO_URL_GEN, SSO_URL_DCM } from "../../configs/configs";
import { USER_TYPE } from "../../configs/constants";
import { NAV_MENU_DOCOMO } from "../../configs/menu-configs-dcm";
import { NAV_MENU_MERCHANT_GEN } from "../../configs/menu-configs-merchant-general";
import { NAV_MENU_MERCHANT_INC } from "../../configs/menu-configs-merchant-inclusive";
import { NAV_MENU_HD } from "../../configs/menu-configs-merchant-hd";
import FlexibleModal from "../UI/modal/FlexibleModal";
import { LABELS } from "./constants";

/** メニュー用のタイプ定義 */
interface Item {
  type: string;
  text: string;
  href: string;
}

interface Permission {
  id: string;
  items: Item[];
}

interface Menu {
  id: number;
  text: string;
  permissions: Permission[];
}

/**
 * サイトナビ項目作成関数
 *
 * @param permissions ログインユーザの権限一覧
 * @returns サイトナビ項目リスト
 */
const createMenuItem = (
  permissions: string[] | undefined,
  menuConfig: Menu[],
  expandedCategoryIds: Set<number>
) => {
  // デフォルトメニュー
  let menuItem: SideNavigationProps.Section[] = [];

  //作成済みカテゴリであるかどうか判定用セット
  let createdCategoryIds = new Set();

  if (permissions && menuConfig) {
    // 権限に応じてメニューに項目を追加
    menuConfig.forEach((category) => {
      category.permissions.forEach((p) => {
        if (permissions.includes(p.id)) {
          //既に作成済みなカテゴリの場合
          const pId = category.id;
          if (createdCategoryIds.has(pId)) {
            // 作成済みなカテゴリのサブメニュを取得
            let createdCategory = menuItem.find(
              (menu) => menu.text === category.text
            );
            let createdSubMenu = createdCategory?.items;

            // 更新するサブメニュを作成
            if (createdSubMenu) {
              createdSubMenu = [
                ...createdSubMenu,
                ...p.items,
              ] as SideNavigationProps.Section[];

              //カテゴリのサブメニューを更新
              menuItem.forEach((el) => {
                if (createdCategory?.text === el.text && createdSubMenu) {
                  el.items = createdSubMenu;
                }
              });
            }
            //作成済みでないカテゴリの場合
          } else {
            menuItem = [
              ...menuItem,
              {
                type: "section",
                text: category.text,
                items: [...p.items],
                defaultExpanded: expandedCategoryIds.has(pId),
              },
            ] as SideNavigationProps.Section[];

            //作成済みカテゴリのIDセットに追加
            createdCategoryIds.add(category.id);
          }
        }
      });
    });
  }

  return menuItem;
};

/**
 * FORMの項目を作成
 *
 * @param key キー
 * @param value 値
 * @returns 作成の項目
 */
const createElement = (key: string, value: string) => {
  let hiddenField = document.createElement("input");
  hiddenField.setAttribute("type", "hidden");
  hiddenField.setAttribute("name", key);
  hiddenField.setAttribute("value", value);
  return hiddenField;
};

/**
 * FORMを作成し、pathのアクションへsubmit
 *
 * @param ssoSessionId ワンタイムパスワード
 */
const openWindow = (ssoSessionId: string, isDocomoUser: boolean) => {
  let form = document.createElement("form");
  form.setAttribute("action", isDocomoUser ? SSO_URL_DCM : SSO_URL_GEN);
  form.setAttribute("target", "_blank");
  form.setAttribute("method", "post");

  form.appendChild(createElement("sso_session_id", ssoSessionId));
  document.body.appendChild(form);
  form.submit();
};

const SideNav = () => {
  const [issueOnetimePassword] = useIssueOnetimePasswordMutation();

  const [activeHref, setActiveHref] = useState("/");
  // 参照グループ選択モーダル
  const [visible, setVisible] = useState(false);

  // 参照範囲選択肢リスト
  const [referenceGroupList, setReferenceGroupList] = useState<
    TilesProps.TilesDefinition[]
  >([]);

  // 参照範囲選択肢
  const [selectReferenceGroup, setSelectReferenceGroup] = useState<string>("");

  // メニューID
  const [mnId, setMnId] = useState<string>();

  const navigate = useNavigate();

  //権限取得,ユーザ種別取得
  const { permissions, companyType, referenceGroups } = useGetAuthQuery(
    undefined,
    {
      selectFromResult: ({ data }) => ({
        permissions: data?.permissions,
        companyType: data?.companyType,
        referenceGroups: data?.referenceGroups,
      }),
    }
  );

  // 事業者種別によるメニューが違う
  const [menuConfig, setMenuConfig] = useState<Menu[]>();

  //　サブメニュの開閉状況
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(
    new Set()
  );

  const [menuItem, setMenuItem] = useState<SideNavigationProps.Section[]>();

  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);

  useEffect(() => {
    if (companyType && permissions) {
      let menuConfig: Menu[];

      switch (companyType) {
        case USER_TYPE.DOCOMO:
          menuConfig = NAV_MENU_DOCOMO;
          break;
        case USER_TYPE.MERCHANT_GEN:
          menuConfig = NAV_MENU_MERCHANT_GEN;
          break;
        case USER_TYPE.MERCHANT_INC:
          menuConfig = NAV_MENU_MERCHANT_INC;
          break;
        case USER_TYPE.HD:
          menuConfig = NAV_MENU_HD;
          break;
        default:
          menuConfig = NAV_MENU_DOCOMO;
      }
      setMenuConfig(menuConfig);

      const menuItem = createMenuItem(
        permissions,
        menuConfig,
        expandedCategoryIds
      );
      setMenuItem(menuItem);
    }
  }, [companyType, permissions, expandedCategoryIds]);

  /**
   * 参照グループを設定
   */
  useEffect(() => {
    if (referenceGroups && referenceGroups.length > 1) {
      const tmpList = referenceGroups.map((group) => ({
        label: group.referenceGroupName,
        value: String(group.referenceGroupId),
      })) as TilesProps.TilesDefinition[];
      setSelectReferenceGroup(tmpList[0].value);
      setReferenceGroupList(tmpList);
    }
  }, [referenceGroups]);

  /**
   * 参照グループを選択関数
   */
  const handleReferenceGroupOnChange = (value: string) => {
    setSelectReferenceGroup(value);
  };

  /**
   * モーダルポジティブ関数
   */
  const positiveFn = useCallback(async () => {
    let result;
    const requestBody = {
      mnId: mnId,
      referenceGroupId: selectReferenceGroup,
    } as OnetimePasswordRequest;
    try {
      result = (await issueOnetimePassword(requestBody)) as {
        data: OnetimePasswordResponse;
      };
      const ssoSessionId = result?.data.ssoSessionId;
      if (ssoSessionId) openWindow(ssoSessionId, isDocomoUser);
    } catch (error) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectReferenceGroup, mnId, isDocomoUser]);

  /**
   * ドコモ画面遷移関数
   */
  const notMultiReferenceGroupClickFn = useCallback(
    async (mnId: string) => {
      let result;
      const requestBody = {
        mnId: mnId,
      } as OnetimePasswordRequest;

      try {
        result = (await issueOnetimePassword(requestBody)) as {
          data: OnetimePasswordResponse;
        };
        const ssoSessionId = result?.data.ssoSessionId;
        if (ssoSessionId) openWindow(ssoSessionId, isDocomoUser);
      } catch (error) {}
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mnId, isDocomoUser]
  );

  return (
    <>
      <FlexibleModal
        visible={visible}
        title={LABELS.TITLE}
        setVisibleFn={setVisible}
        positiveFn={positiveFn}
      >
        {referenceGroupList && referenceGroupList.length > 1 && (
          <SpaceBetween size="l">
            {LABELS.ATTENTION}
            <Tiles
              onChange={({ detail }) =>
                handleReferenceGroupOnChange(detail.value)
              }
              value={selectReferenceGroup}
              items={referenceGroupList}
            />
          </SpaceBetween>
        )}
      </FlexibleModal>
      <SideNavigation
        activeHref={activeHref}
        onChange={(event) => {
          const isExpanded = event.detail.expanded;

          // カテゴリの矢印を押下時のサブメニュ開閉制御
          menuConfig?.forEach((c) => {
            if (c.text === event.detail.item.text) {
              let updatedExpandedCategoryIds = expandedCategoryIds;
              // 開く場合
              if (isExpanded) {
                updatedExpandedCategoryIds.add(c.id);
                // 閉じる場合
              } else {
                updatedExpandedCategoryIds.delete(c.id);
              }
              setExpandedCategoryIds(updatedExpandedCategoryIds);
            }
          });
        }}
        onFollow={async (event) => {
          if (!event.detail.external) {
            const href = event.detail.href;
            event.preventDefault();
            setActiveHref(href);

            //画面遷移
            const regex = /\/sso\?mnId=/;
            const isCharacterPresent = href.match(regex) !== null;
            // SSO連携画面別タブ
            if (isCharacterPresent) {
              const mnId = href.substring(10);
              setMnId(mnId);

              // 参照グループを選択させる
              if (referenceGroups && referenceGroups?.length > 1) {
                setVisible(true);
              } else {
                notMultiReferenceGroupClickFn(mnId);
              }
            } else {
              // それ以外既存タブで
              navigate(href);
            }

            // サブメニュ開閉制御
            menuConfig?.forEach((c) => {
              c.permissions.forEach((p) => {
                p.items.forEach((i) => {
                  if (i.href === href) {
                    let updatedExpandedCategoryIds = expandedCategoryIds;
                    updatedExpandedCategoryIds.add(c.id);
                    setExpandedCategoryIds(updatedExpandedCategoryIds);
                  }
                });
              });
            });
          }
        }}
        items={menuItem}
      />
    </>
  );
};

export default SideNav;
